/**
 * `/congress/symbol/{symbol}?days=` — Senate and House trades in one stock
 * (`services/qv-data/app/congress/read.py`, `docs/plans/congress-trades.md`).
 *
 * Every field is mapped by name, as `api/holdings.ts`: a field the server
 * renames fails here, in a type, rather than going quietly missing.
 */
import { requestHeaders } from "../lib/user";
import { httpBase } from "./backend";

const BASE = (httpBase(import.meta.env.VITE_QV_SYM_URL, import.meta.env.VITE_QV_SET_URL) || "").replace(/\/$/, "");

export type CongressSide = "buy" | "sell" | "other";

export interface CongressTrade {
  disclosed: string;
  traded: string | null;
  /** Disclosed more than 45 days after the trade — past the STOCK Act's limit. */
  late: boolean;
  chamber: "House" | "Senate" | string;
  firstName: string;
  lastName: string;
  district: string | null;
  owner: string | null;
  type: string;
  side: CongressSide;
  amount: string | null;
  amountLow: number | null;
  amountHigh: number | null;
  link: string | null;
}

export interface CongressSummary {
  buys: number;
  sells: number;
  buyLow: number | null;
  buyHigh: number | null;
  sellLow: number | null;
  sellHigh: number | null;
  members: number;
  house: number;
  senate: number;
}

export interface CongressSymbol {
  symbol: string;
  days: number;
  summary: CongressSummary;
  trades: CongressTrade[];
  newestDisclosure: string | null;
}

const num = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? v : 0);
const opt = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);
const str = (v: unknown): string | null => (typeof v === "string" && v !== "" ? v : null);

export function mapCongress(j: any): CongressSymbol {
  const s = j?.summary ?? {};
  return {
    symbol: String(j?.symbol ?? ""),
    days: num(j?.days),
    summary: {
      buys: num(s.buys), sells: num(s.sells),
      buyLow: opt(s.buyLow), buyHigh: opt(s.buyHigh), sellLow: opt(s.sellLow), sellHigh: opt(s.sellHigh),
      members: num(s.members), house: num(s.house), senate: num(s.senate),
    },
    trades: (Array.isArray(j?.trades) ? j.trades : []).map((t: any): CongressTrade => ({
      disclosed: String(t.disclosed),
      traded: str(t.traded),
      late: Boolean(t.late),
      chamber: String(t.chamber ?? ""),
      firstName: String(t.firstName ?? ""),
      lastName: String(t.lastName ?? ""),
      district: str(t.district),
      owner: str(t.owner),
      type: String(t.type ?? ""),
      side: t.side === "buy" || t.side === "sell" ? t.side : "other",
      amount: str(t.amount),
      amountLow: opt(t.amountLow),
      amountHigh: opt(t.amountHigh),
      link: str(t.link),
    })),
    newestDisclosure: str(j?.newestDisclosure),
  };
}

/** null when the server has no Congress data yet (503) or cannot be reached. */
export async function loadCongress(symbol: string, days: number): Promise<CongressSymbol | null> {
  if (!BASE) return null;
  try {
    const res = await fetch(`${BASE}/congress/symbol/${encodeURIComponent(symbol)}?days=${days}`, {
      headers: await requestHeaders(),
    });
    if (!res.ok) return null;
    return mapCongress(await res.json());
  } catch {
    return null;
  }
}
